import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import {
  ArrowLeft, RefreshCw, Send, CheckCircle2, XCircle,
  AlertTriangle, Activity,
} from 'lucide-react';

// ── Types for the data we display ─────────────────────────────────────────────

interface SemmyActionRow {
  id: string;
  created_at: string;
  client_name: string;
  action_type: string;
  stakes_score: number;
  requires_approval: boolean;
  status: string;
  draft_content: string;
}

interface SemmyOutcomeRow {
  id: string;
  created_at: string;
  client_name: string;
  signal_status: string;
  outcome_quality: string | null;
  fee_modifier: number | null;
}

// Counts that tell us whether each edge function has been firing recently
interface ConnectionStatus {
  supabaseReachable: boolean;
  inboundLastHour: number;    // rows created in last hour → semmy-inbound activity
  outboundLastHour: number;   // rows sent in last hour → semmy-outbound activity
  signalSendLastHour: number; // outcomes sent in last hour → semmy-signal-send activity
}

// ── Visual config ──────────────────────────────────────────────────────────────

const ACTION_STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  queued:   { bg: 'var(--warning-bg)',          text: 'var(--warning-text)' },
  approved: { bg: 'var(--accent-dim)',           text: 'var(--accent)' },
  rejected: { bg: 'var(--danger-bg)',            text: 'var(--danger-text)' },
  sent:     { bg: 'var(--success-bg)',            text: 'var(--success-bright)' },
};

const ACTION_STATUS_LABEL: Record<string, string> = {
  queued:   'in wachtrij',
  approved: 'goedgekeurd',
  rejected: 'afgewezen',
  sent:     'verstuurd',
};

const SIGNAL_STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  pending_approval: { bg: 'var(--warning-bg)',           text: 'var(--warning-text)' },
  approved:         { bg: 'var(--accent-dim)',            text: 'var(--accent)' },
  dismissed:        { bg: 'var(--bg-input)',              text: 'var(--text-muted)' },
  sent:             { bg: 'var(--success-bg)',             text: 'var(--success-bright)' },
  responded:        { bg: 'rgba(138,126,154,0.12)',       text: '#9B8FB0' },
  no_response:      { bg: 'var(--bg-input)',              text: 'var(--text-muted)' },
};

const QUALITY_STYLE: Record<string, { bg: string; text: string }> = {
  genuine:    { bg: 'var(--success-bg)',    text: 'var(--success-bright)' },
  hollow:     { bg: 'var(--warning-bg)',    text: 'var(--warning-text)' },
  cautionary: { bg: 'var(--danger-bg)',     text: 'var(--danger-text)' },
  unknown:    { bg: 'var(--bg-input)',      text: 'var(--text-muted)' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function ago(dateStr: string) {
  try { return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: nl }); }
  catch { return '—'; }
}

function clip(s: string, n: number) {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// ── Small reusable components ─────────────────────────────────────────────────

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className="inline-block w-2 h-2 rounded-full shrink-0"
      style={{ background: ok ? 'var(--success-bright)' : 'var(--danger-dot)' }} />
  );
}

function Chip({ text, style }: { text: string; style?: { bg: string; text: string } }) {
  if (!style) {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded"
        style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}>
        {text}
      </span>
    );
  }
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
      style={{ background: style.bg, color: style.text }}>
      {text}
    </span>
  );
}

// A labelled status card for the Connection Status section
function StatusCard({
  label, ok, value, sub,
}: {
  label: string;
  ok: boolean;
  value: string;
  sub: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <StatusDot ok={ok} />
        <p className="text-[11px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{label}</p>
      </div>
      <p className="text-[14px] font-bold" style={{ color: 'var(--text-primary)' }}>{value}</p>
      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{sub}</p>
    </div>
  );
}

// A consistent section wrapper so all four sections look the same
function Section({
  title, subtitle, children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border overflow-hidden"
      style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}>
      <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--border-separator)' }}>
        <p className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</p>
        {subtitle && (
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

// Column header row — same style for both tables
function TableHead({ cols }: { cols: string[] }) {
  return (
    <div className="flex px-5 py-2 border-b gap-3"
      style={{ borderColor: 'var(--border-separator)' }}>
      {cols.map(c => (
        <p key={c} className="text-[9px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--text-muted)', minWidth: 0 }}>
          {c}
        </p>
      ))}
    </div>
  );
}

// ── Empty and loading states ───────────────────────────────────────────────────

function EmptyRow({ message }: { message: string }) {
  return (
    <div className="px-5 py-6 text-center">
      <p className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{message}</p>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function SemmyDebugPage() {
  const { effectivePartnerId } = useAuth();
  const [actions,    setActions]    = useState<SemmyActionRow[]>([]);
  const [outcomes,   setOutcomes]   = useState<SemmyOutcomeRow[]>([]);
  const [connStatus, setConnStatus] = useState<ConnectionStatus | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Live test form
  const [testMessage, setTestMessage]   = useState('');
  const [testLoading, setTestLoading]   = useState(false);
  const [testResult,  setTestResult]    = useState<{ ok: boolean; msg: string } | null>(null);

  // Fetch all data in parallel — one round-trip to Supabase
  const loadAll = useCallback(async () => {
    if (!isSupabaseConfigured) {
      setConnStatus({
        supabaseReachable: false,
        inboundLastHour: 0,
        outboundLastHour: 0,
        signalSendLastHour: 0,
      });
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const oneHourAgo = new Date(Date.now() - 3_600_000).toISOString();

    const [actionsRes, outcomesRes, inboundRes, outboundRes, signalRes] = await Promise.all([
      // Last 20 actions — proves semmy-inbound is inserting rows
      supabase
        .from('semmy_actions')
        .select('id,created_at,client_name,action_type,stakes_score,requires_approval,status,draft_content')
        .order('created_at', { ascending: false })
        .limit(20),

      // Last 10 outcomes — proves semmy-signal-send is marking rows sent
      supabase
        .from('semmy_outcomes')
        .select('id,created_at,client_name,signal_status,outcome_quality,fee_modifier')
        .order('created_at', { ascending: false })
        .limit(10),

      // Count of actions created in last hour → semmy-inbound activity
      supabase
        .from('semmy_actions')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', oneHourAgo),

      // Count of actions sent in last hour → semmy-outbound activity
      supabase
        .from('semmy_actions')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'sent')
        .gte('sent_at', oneHourAgo),

      // Count of outcomes sent in last hour → semmy-signal-send activity
      supabase
        .from('semmy_outcomes')
        .select('id', { count: 'exact', head: true })
        .eq('signal_status', 'sent')
        .gte('signal_sent_at', oneHourAgo),
    ]);

    setActions((actionsRes.data ?? []) as unknown as SemmyActionRow[]);
    setOutcomes((outcomesRes.data ?? []) as unknown as SemmyOutcomeRow[]);
    setConnStatus({
      supabaseReachable: !actionsRes.error,
      inboundLastHour:    inboundRes.count  ?? 0,
      outboundLastHour:   outboundRes.count ?? 0,
      signalSendLastHour: signalRes.count   ?? 0,
    });
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRefresh = () => { setRefreshing(true); loadAll(); };

  // Inserts a test row directly into semmy_actions so we can exercise the
  // dashboard approval flow without needing a real WhatsApp message.
  const handleTestSend = async () => {
    if (!testMessage.trim()) return;
    setTestLoading(true);
    setTestResult(null);

    if (!isSupabaseConfigured) {
      setTestResult({ ok: false, msg: 'Supabase is niet geconfigureerd in deze omgeving' });
      setTestLoading(false);
      return;
    }

    const { error } = await supabase.from('semmy_actions').insert({
      action_type:       'first_contact',
      status:            'queued',
      draft_content:     testMessage.trim(),
      approved_content:  null,
      client_id:         'test-client',
      client_name:       'Test Client',
      client_email:      null,
      client_status:     'new',
      sentiment_score:   0.7,
      stakes_score:      4,
      stakes_inputs:     {},
      requires_approval: true,
      approval_reason:   'Debug test insertion',
      autonomous_send:   false,
      partner_id:        effectivePartnerId ?? 'unknown',
    });

    if (error) {
      setTestResult({ ok: false, msg: error.message });
    } else {
      setTestResult({ ok: true, msg: 'Rij ingevoegd — tabel wordt zo vernieuwd' });
      setTestMessage('');
      loadAll();
    }
    setTestLoading(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5 max-w-7xl mx-auto animate-fade-up">

      {/* ── Header ── */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-3">
          <Link
            to="/partner/semmy"
            className="flex items-center gap-1.5 text-[11px] transition-opacity hover:opacity-70"
            style={{ color: 'var(--text-muted)' }}>
            <ArrowLeft size={11} />
            Terug naar Dashboard
          </Link>
          <span style={{ color: 'var(--border-separator)' }}>·</span>
          <div className="flex items-center gap-2">
            <Activity size={14} style={{ color: 'var(--text-muted)' }} />
            <h1 className="text-[16px] font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Semmy Debug
            </h1>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading || refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] border transition-all"
          style={{
            color: 'var(--text-muted)',
            borderColor: 'var(--border-primary)',
            background: 'var(--bg-input)',
            opacity: loading || refreshing ? 0.5 : 1,
          }}>
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
          Vernieuwen
        </button>
      </div>

      {/* ── Section 4: Connection Status ── */}
      {/* Shown first because it gives the fastest overview of system health */}
      <Section
        title="Verbindingsstatus"
        subtitle="Groen = bereikbaar / actief. Getallen tonen edge function activiteit in de afgelopen 60 minuten.">
        <div className="px-5 py-5 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <StatusCard
            label="Supabase"
            ok={connStatus?.supabaseReachable ?? false}
            value={loading ? '…' : connStatus?.supabaseReachable ? 'Verbonden' : 'Onbereikbaar'}
            sub="semmy_actions table"
          />
          <StatusCard
            label="semmy-inbound"
            ok={(connStatus?.inboundLastHour ?? 0) > 0}
            value={loading ? '…' : `${connStatus?.inboundLastHour ?? 0} rows`}
            sub="aangemaakt afgelopen uur"
          />
          <StatusCard
            label="semmy-outbound"
            ok={(connStatus?.outboundLastHour ?? 0) > 0}
            value={loading ? '…' : `${connStatus?.outboundLastHour ?? 0} sent`}
            sub="acties afgelopen uur"
          />
          <StatusCard
            label="semmy-signal-send"
            ok={(connStatus?.signalSendLastHour ?? 0) > 0}
            value={loading ? '…' : `${connStatus?.signalSendLastHour ?? 0} sent`}
            sub="signalen afgelopen uur"
          />
        </div>
      </Section>

      {/* ── Section 1: Recent Actions ── */}
      {/* Every inbound WhatsApp creates a row here, so this is the primary proof
          that semmy-inbound is receiving and processing messages. */}
      <Section
        title="Recente acties — semmy_actions"
        subtitle="Laatste 20 rijen, nieuwste eerst. Elk inkomend WhatsApp-bericht maakt hier een rij aan.">
        <div className="overflow-x-auto">
          {/* Column headers */}
          <div className="flex items-center gap-0 min-w-[760px]">
            {[
              { label: 'Time',     w: '130px' },
              { label: 'Client',   w: '140px' },
              { label: 'Type',     w: '130px' },
              { label: 'Stakes',   w: '70px'  },
              { label: 'Approval', w: '100px' },
              { label: 'Status',   w: '80px'  },
              { label: 'Content',  w: '1fr'   },
            ].map(col => (
              <div key={col.label}
                className="px-3 py-2 text-[9px] font-semibold uppercase tracking-wider border-b"
                style={{ minWidth: col.w, borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                {col.label}
              </div>
            ))}
          </div>

          {loading && <EmptyRow message="Loading…" />}
          {!loading && actions.length === 0 && (
            <EmptyRow message="Nog geen rijen in semmy_actions." />
          )}

          {actions.map((a, i) => (
            <div key={a.id}
              className="flex items-center min-w-[760px] border-b"
              style={{
                borderColor: 'var(--border-separator)',
                background: i % 2 === 0 ? 'transparent' : 'var(--bg-input)',
              }}>
              <div className="px-3 py-2.5 text-[11px] shrink-0" style={{ minWidth: '130px', color: 'var(--text-muted)' }}>
                {ago(a.created_at)}
              </div>
              <div className="px-3 py-2.5 text-[11px] font-medium truncate shrink-0" style={{ minWidth: '140px', color: 'var(--text-primary)' }}>
                {a.client_name}
              </div>
              <div className="px-3 py-2.5 text-[11px] truncate shrink-0" style={{ minWidth: '130px', color: 'var(--text-secondary)' }}>
                {a.action_type.replace(/_/g, ' ')}
              </div>
              <div className="px-3 py-2.5 shrink-0" style={{ minWidth: '70px' }}>
                <span className="text-[11px] font-semibold"
                  style={{ color: a.stakes_score >= 7 ? 'var(--danger-text)' : a.stakes_score >= 4 ? 'var(--warning-text)' : 'var(--text-muted)' }}>
                  {a.stakes_score}/10
                </span>
              </div>
              <div className="px-3 py-2.5 shrink-0" style={{ minWidth: '100px' }}>
                <Chip
                  text={a.requires_approval ? 'vereist' : 'autonoom'}
                  style={a.requires_approval
                    ? { bg: 'var(--warning-bg)', text: 'var(--warning-text)' }
                    : { bg: 'var(--accent-dim)', text: 'var(--accent)' }}
                />
              </div>
              <div className="px-3 py-2.5 shrink-0" style={{ minWidth: '80px' }}>
                <Chip
                  text={ACTION_STATUS_LABEL[a.status] ?? a.status}
                  style={ACTION_STATUS_STYLE[a.status]}
                />
              </div>
              <div className="px-3 py-2.5 flex-1 min-w-0">
                <span className="text-[10px]" style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                  {clip(a.draft_content, 60)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Section 2: Recent Outcomes ── */}
      {/* Rows here show that signal flow is working end-to-end */}
      <Section
        title="Recente resultaten — semmy_outcomes"
        subtitle="Laatste 10 rijen, nieuwste eerst. Rijen verschijnen hier wanneer de operator een taakresultaat registreert.">
        <div className="overflow-x-auto">
          {/* Column headers */}
          <div className="flex items-center min-w-[560px]">
            {[
              { label: 'Time',           w: '130px' },
              { label: 'Client',         w: '160px' },
              { label: 'Signal Status',  w: '140px' },
              { label: 'Quality',        w: '110px' },
              { label: 'Fee modifier',   w: '110px' },
            ].map(col => (
              <div key={col.label}
                className="px-3 py-2 text-[9px] font-semibold uppercase tracking-wider border-b"
                style={{ minWidth: col.w, borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                {col.label}
              </div>
            ))}
          </div>

          {loading && <EmptyRow message="Loading…" />}
          {!loading && outcomes.length === 0 && (
            <EmptyRow message="Nog geen resultaten." />
          )}

          {outcomes.map((o, i) => (
            <div key={o.id}
              className="flex items-center min-w-[560px] border-b"
              style={{
                borderColor: 'var(--border-separator)',
                background: i % 2 === 0 ? 'transparent' : 'var(--bg-input)',
              }}>
              <div className="px-3 py-2.5 text-[11px] shrink-0" style={{ minWidth: '130px', color: 'var(--text-muted)' }}>
                {ago(o.created_at)}
              </div>
              <div className="px-3 py-2.5 text-[11px] font-medium truncate shrink-0" style={{ minWidth: '160px', color: 'var(--text-primary)' }}>
                {o.client_name}
              </div>
              <div className="px-3 py-2.5 shrink-0" style={{ minWidth: '140px' }}>
                <Chip
                  text={o.signal_status.replace(/_/g, ' ')}
                  style={SIGNAL_STATUS_STYLE[o.signal_status]}
                />
              </div>
              <div className="px-3 py-2.5 shrink-0" style={{ minWidth: '110px' }}>
                {o.outcome_quality ? (
                  <Chip
                    text={o.outcome_quality}
                    style={QUALITY_STYLE[o.outcome_quality]}
                  />
                ) : (
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>—</span>
                )}
              </div>
              <div className="px-3 py-2.5 text-[11px] shrink-0" style={{ minWidth: '110px' }}>
                {o.fee_modifier != null ? (
                  <span className="font-semibold"
                    style={{ color: o.fee_modifier >= 1 ? 'var(--success-bright)' : o.fee_modifier > 0 ? 'var(--warning-text)' : 'var(--danger-text)' }}>
                    {(o.fee_modifier * 100).toFixed(0)}%
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Section 3: Live Test ── */}
      {/* Inserts a fake action directly so we can test the approval flow in the
          dashboard without needing a real WhatsApp message to come in first. */}
      <Section
        title="Live test — simuleer inkomend bericht"
        subtitle="Voegt direct een rij in semmy_actions in zodat je de goedkeuringsflow kunt testen zonder echt WhatsApp-bericht.">
        <div className="px-5 py-5 space-y-3">
          <div className="flex items-start gap-3">
            <textarea
              value={testMessage}
              onChange={e => setTestMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleTestSend(); }}
              placeholder="Typ een testbericht… bijv. 'Hoi, ik heb een vraag over mijn offerte'"
              rows={2}
              className="flex-1 rounded-lg p-3 text-[12px] leading-relaxed resize-none outline-none"
              style={{
                background: 'var(--bg-input)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-primary)',
              }}
            />
            <button
              onClick={handleTestSend}
              disabled={testLoading || !testMessage.trim()}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[12px] font-semibold shrink-0 transition-all"
              style={{
                background: 'var(--accent)',
                color: '#fff',
                opacity: testLoading || !testMessage.trim() ? 0.5 : 1,
              }}>
              {testLoading
                ? <RefreshCw size={12} className="animate-spin" />
                : <Send size={12} />}
              Test versturen
            </button>
          </div>

          {/* Result feedback */}
          {testResult && (
            <div className="flex items-center gap-2 text-[12px] p-3 rounded-lg"
              style={{
                background: testResult.ok ? 'var(--success-bg)' : 'var(--danger-bg)',
                border: `1px solid ${testResult.ok ? 'var(--success-border)' : 'var(--danger-border)'}`,
                color: testResult.ok ? 'var(--success-bright)' : 'var(--danger-text)',
              }}>
              {testResult.ok
                ? <CheckCircle2 size={13} />
                : <XCircle size={13} />}
              {testResult.msg}
            </div>
          )}

          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            Inserts with: action_type = first_contact · status = queued · stakes_score = 4 ·
            requires_approval = true · partner_id = {effectivePartnerId ?? 'unknown'} · client_id = test-client
          </p>
        </div>
      </Section>

    </div>
  );
}
